interface SolidgateErrorPayload {
  title: string;
  description: string;
}

/**
 * @see https://docs.solidgate.com/payments/payments-insights/error-codes
 */
export const solidgateErrorsConfig: Record<string, SolidgateErrorPayload> = {
  '0.01': {
    title: 'Your bank declined the payment.',
    description:
      "This can sometimes happen for various reasons. Please try making the payment again. If it still doesn't work, trying a little later or using a different card usually helps.",
  },
  '0.02': {
    title: 'Your session has expired.',
    description: 'For your security, the payment window timed out. No problem at all—please start the checkout process again.',
  },
  '0.03': {
    title: "This payment method can't be used.",
    description:
      'This may be due to regional restrictions or card limitations. Please try a different payment card to complete your purchase.',
  },

  '1.01': {
    title: 'Authentication Failed.',
    description: 'There was an issue verifying your payment. Please attempt the payment one more time.',
  },

  '2.01': {
    title: 'There seems to be an issue with the payment details.',
    description:
      "Something doesn't look right with the information provided. Please double-check that all your payment details are correct and try again.",
  },
  '2.02': {
    title: 'The payment amount seems incorrect.',
    description:
      "Please check that the amount entered is correct and that it doesn't exceed your card's limit. Using another card might also solve the issue.",
  },
  '2.03': {
    title: 'Unsupported currency.',
    description:
      "This card doesn't support the currency selected for this transaction. Please choose one of the available supported currencies and try again.",
  },
  '2.05': {
    title: 'This order seems to have expired.',
    description:
      'This can happen if the payment has already been completed or cancelled. Please try starting a new transaction from our website.',
  },
  '2.06': {
    title: 'The CVV code is incorrect.',
    description:
      'Please re-enter the 3 or 4-digit security code from the back of your card. If the issue continues, please confirm the code with your bank.',
  },
  '2.07': {
    title: 'Some information is missing.',
    description:
      'It looks like some required fields were left empty. Please ensure all payment information is filled in completely and try again.',
  },
  '2.08': {
    title: 'The card number seems incorrect.',
    description: "Please carefully re-enter your card number and try again. If that doesn't work, you may want to use a different card.",
  },
  '2.09': {
    title: 'The expiration date is invalid.',
    description:
      'Please double-check your card’s expiration date (MM/YY) and enter it again. If your card has expired, please use a different one.',
  },
  '2.10': {
    title: 'There was an issue with the security verification.',
    description:
      "It seems the extra security step (often called 3D Secure) wasn't completed. Please try again and complete all verification steps required by your bank.",
  },
  '2.11': {
    title: 'Your bank had a problem with the security verification.',
    description:
      "The extra security check required by your bank couldn't be completed. Please try the payment again. If it persists, using another card may help.",
  },
  '2.12': {
    title: 'Security verification was cancelled.',
    description:
      'The final security step to confirm the payment was not completed. Please try again and be sure to complete the entire verification process.',
  },
  '2.13': {
    title: "There's an issue with your connection's location.",
    description:
      "For security reasons, we couldn't verify your location. Please ensure you are not using a VPN or proxy and try the payment again.",
  },
  '2.14': {
    title: "There's an issue with your subscription.",
    description:
      'We encountered a problem with your subscription details, like a duplicate charge attempt. Please review your active subscriptions. If the issue persists, our support team is ready to help.',
  },
  '2.15': {
    title: 'Extra security verification is required.',
    description:
      "Your bank requires an additional step to confirm it's you. Please try the payment again and complete the 3D Secure verification when prompted.",
  },
  '2.16': {
    title: 'A previous subscription payment is still in progress.',
    description:
      'Please wait a few moments for your last payment to complete before trying again. If this message continues to appear, please contact our support team.',
  },
  '2.17': {
    title: 'The coupon code is not valid.',
    description:
      'The coupon you entered has expired or is inactive. Please try a different code or continue your purchase without a coupon.',
  },
  '2.18': {
    title: 'The selected item is invalid.',
    description: 'It seems there is an issue with the product selected. Please go back, select your product again, and retry the payment.',
  },

  '3.01': {
    title: 'Your bank has blocked this card.',
    description:
      'This is usually a temporary measure. Please contact your bank to ask them to unblock the card, and then feel free to try again.',
  },
  '3.02': {
    title: 'Insufficient Funds.',
    description: "Your account doesn't have enough funds to cover this purchase. Please top up your account or use a different card.",
  },
  '3.03': {
    title: 'The payment exceeds your card limit.',
    description:
      'This transaction is over the spending limit set on your card. You can ask your bank to increase the limit or simply try another card.',
  },
  '3.04': {
    title: 'Your bank has declined the transaction.',
    description:
      'Your bank has not specified a reason. We recommend contacting them for more details. Alternatively, trying a different card often works.',
  },
  '3.05': {
    title: 'Your bank requests you to call them.',
    description: "To proceed, your bank needs you to contact them directly. Once you've spoken with them, please try the payment again.",
  },
  '3.06': {
    title: 'This card type is not supported.',
    description:
      "It looks like you're using a debit card that we can't process. Please try a credit card or another supported payment method.",
  },
  '3.07': {
    title: 'This card brand is not supported.',
    description: "We don't currently support this card brand (e.g., Visa, Mastercard). Please try a card from a different provider.",
  },
  '3.08': {
    title: 'Your bank declined the payment.',
    description:
      'Your bank has declined the transaction without providing a specific reason. Please try the payment again, or use a different card. If the problem continues, contacting your bank is the best next step.',
  },
  '3.09': {
    title: 'Extra security verification is needed.',
    description:
      'Your bank requires an additional security check (3D Secure) for this payment. Please try again and follow the prompts for verification.',
  },
  '3.10': {
    title: 'Your bank flagged this as a potential fraud risk.',
    description: 'This is a security measure from your bank. Please contact them to confirm this payment is legitimate, then try again.',
  },
  '3.11': {
    title: 'Your recurring payment was cancelled.',
    description:
      'It appears the recurring payment for your subscription has been cancelled. For help reactivating it, please contact our support team.',
  },
  '3.12': {
    title: 'Your bank account appears to be closed.',
    description:
      'The transaction was declined because the associated bank account may be closed. Please use a different card or contact your bank for more information.',
  },

  '4.01': {
    title: 'This card cannot be used.',
    description: 'For security reasons, this card has been blocked in our system. Please use a different card to complete your payment.',
  },
  '4.02': {
    title: 'This card has been reported as stolen.',
    description: 'The transaction was declined for your protection. Please contact your bank immediately to secure your account.',
  },
  '4.03': {
    title: 'This card has restrictions.',
    description:
      'Your bank may have placed restrictions on this card (e.g., for online or international use). Please check with your bank or use a different card.',
  },
  '4.04': {
    title: 'This card has been reported as lost.',
    description:
      'For your security, this transaction has been declined. Please use a different card and contact your bank about the lost card.',
  },
  '4.05': {
    title: 'Our security system blocked this payment.',
    description:
      'Our automated security system has flagged this transaction. Please double-check your details and try again. If it still fails, please contact our support team.',
  },
  '4.06': {
    title: 'Payment blocked due to location.',
    description:
      'We were unable to process your payment due to country or IP restrictions. If you believe this is an error, please contact support for assistance.',
  },
  '4.07': {
    title: 'Our security system blocked this transaction.',
    description:
      'To protect your account, our security system has declined this payment. Please try again in a little while. If the problem persists, please contact support.',
  },
  '4.08': {
    title: 'The billing address does not match.',
    description:
      'The billing address you entered does not match the address your bank has on file for this card. Please check and re-enter the correct billing address.',
  },
  '4.09': {
    title: 'Our security system declined the payment.',
    description:
      'This transaction was declined by our fraud prevention system. If you believe this is a mistake, please contact our support team for help.',
  },

  '5.01': {
    title: 'Security verification failed.',
    description:
      'The extra security step from your bank (3D Secure) did not succeed. Please try the payment again and carefully follow the instructions.',
  },
  '5.02': {
    title: 'There was an issue with your saved card.',
    description:
      'The token for your saved payment method is no longer valid. Please remove the card and add it again, or use a different payment method.',
  },
  '5.03': {
    title: 'A temporary technical issue occurred.',
    description:
      "We're facing a temporary hiccup on our end. Please wait a moment and try your payment again. Using another card might also help.",
  },
  '5.04': {
    title: 'There is a payment configuration issue.',
    description: "We're experiencing a technical issue with our payment setup. Our team is on it. Please try again in a little while.",
  },
  '5.05': {
    title: 'Payments for this service are not yet active.',
    description: 'We are not quite ready to accept payments yet. Please check back with us again shortly.',
  },
  '5.06': {
    title: 'This looks like a duplicate payment.',
    description:
      'We noticed a very similar transaction was just submitted. If this was intentional, please wait a minute before trying again. Otherwise, please check your recent orders.',
  },
  '5.07': {
    title: 'Our service is currently busy.',
    description: "We're experiencing high traffic at the moment. Please wait just a moment and then try your payment again.",
  },
  '5.08': {
    title: 'The transaction is invalid.',
    description:
      'The payment request contained invalid information. Please start the checkout process again. If the issue persists, trying another card may help.',
  },
  '5.09': {
    title: 'There seems to be a configuration issue.',
    description:
      'We are experiencing a technical problem on our side. Our team has been notified. Please contact our support team for assistance.',
  },
  '5.10': {
    title: "This payment method isn't supported.",
    description:
      'The requested payment method is not supported by our processor. Please contact our support team for more information on available options.',
  },
  '5.11': {
    title: 'A routing error occurred.',
    description: 'We encountered a temporary technical issue while processing your payment. Please try again in a few moments.',
  },
  '5.12': {
    title: 'Your account is currently blocked.',
    description:
      'For security reasons, your account has been temporarily blocked from making payments. Please contact our support team for help.',
  },

  '6.01': {
    title: 'An unknown error occurred.',
    description:
      "We're not sure what went wrong. Please try the payment again. If it continues to fail, using a different card may solve the issue.",
  },
  '6.02': {
    title: 'A connection error occurred.',
    description: "We couldn't establish a secure connection to the payment provider. Please check your internet connection and try again.",
  },
  '6.03': {
    title: 'There was a processing issue.',
    description:
      "A temporary problem occurred while processing your payment. Please give it another try. If it still doesn't work, please contact support.",
  },

  '7.01': {
    title: 'There was an issue with your saved card.',
    description:
      "We couldn't find the details for your saved card. Please try re-entering your card information or adding it as a new payment method.",
  },
  '7.02': {
    title: 'There was a problem with Google Pay.',
    description:
      "We couldn't process your Google Pay transaction. Please check your Google Pay settings and try again, or select a different payment method.",
  },
  '7.03': {
    title: 'A token error occurred.',
    description: 'There was a temporary technical issue with creating a secure token for your payment. Please try the payment again.',
  },
  '7.04': {
    title: 'An authentication error occurred.',
    description: 'A technical error occurred during the security verification step. Please try your payment again.',
  },
  '7.05': {
    title: 'There was a problem with Apple Pay.',
    description:
      "We couldn't process your Apple Pay transaction. Please check your Apple Pay settings and try again, or select a different payment method.",
  },
  '7.06': {
    title: 'There was an issue creating a secure payment token.',
    description:
      'A technical problem prevented us from securely processing your details. Please double-check your payment information and try again.',
  },
  '7.07': {
    title: 'A technical error occurred during verification.',
    description:
      'A required piece of security information was missing. Please try the payment again, ensuring you complete all authentication steps.',
  },
};

export const getSolidgateError = (code: string | undefined): SolidgateErrorPayload => {
  return code
    ? solidgateErrorsConfig?.[code]
    : {
        title: 'An internal error occurred while processing your payment.',
        description: 'This can happen for various reasons. Please try again later or contact our support team for assistance.',
      };
};
